import type { RefObject } from 'react';
import { useMemo } from 'react';

import { CaretDownIcon, CaretUpIcon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';
import { useSessionStorageValue } from 'src/app/shared/hooks/storage';

import type { TaskShape } from '../../types';

import { useTasks } from './useTasks';

const DEFAULT_TASKS_DISPLAY_COUNT = 3;
const NEXT_STEPS_EXPANDED_STORAGE_KEY = 'starterDashboardV2_nextStepsExpanded';

type UseNextStepsReturn = ModelHookResult<
	{
		tasks: TaskShape[];
		stats: {
			completed: number;
			count: number;
		};
		showDownloadFlyerTask: boolean;
		showTaskListToggle: boolean;
		toggleTaskListLabel: TranslationKey;
		toggleTaskListIcon: React.ReactNode;
		containerRef: RefObject<null>;
		viewMoreRef?: RefObject<null>;
		getListItemRef: (itemIndex: number) => RefObject<null> | undefined;
		taskListIsExpanded: boolean;
	},
	{
		toggleTaskList: () => void;
	}
>;

export function useNextSteps(): UseNextStepsReturn {
	const { tasks: allTasks, stats } = useTasks();
	const { containerRef, loadMoreRef, getListItemRef, recordListEvent } = useLazyLoadedListFocus();
	const [expandedStorageValue, setExpandedStorageValue] = useSessionStorageValue(NEXT_STEPS_EXPANDED_STORAGE_KEY);
	const taskListIsExpanded = expandedStorageValue === 'true';

	const showTaskListToggle = allTasks.length > DEFAULT_TASKS_DISPLAY_COUNT;

	const toggleTaskListLabel = taskListIsExpanded
		? translationKey('corgi.petition.starter_dashboard.steps_to_victory.view_less_label')
		: translationKey('corgi.petition.starter_dashboard.steps_to_victory.view_more_label');

	const toggleTaskListIcon = taskListIsExpanded ? (
		<CaretUpIcon className="icon-strong align-middle" size={20} />
	) : (
		<CaretDownIcon className="icon-strong align-middle" size={20} />
	);

	const trimmedTasks = useMemo(() => allTasks.slice(0, DEFAULT_TASKS_DISPLAY_COUNT), [allTasks]);
	const displayedTasks = taskListIsExpanded ? allTasks : trimmedTasks;

	const toggleTaskList = () => {
		if (!taskListIsExpanded) {
			recordListEvent({ type: 'loadMore', itemsCount: displayedTasks.length, triggerFocusFallback: true });
		}
		setExpandedStorageValue(taskListIsExpanded ? null : 'true');
	};

	const viewMoreRef = !taskListIsExpanded ? loadMoreRef : undefined;

	const {
		data: {
			featureConfigs: { multipleQrCodeFlyersEnabled },
		},
	} = useStarterDashboardV2PageData();
	const showDownloadFlyerTask = multipleQrCodeFlyersEnabled;

	return {
		data: {
			tasks: displayedTasks,
			stats,
			showTaskListToggle,
			showDownloadFlyerTask,
			toggleTaskListLabel,
			toggleTaskListIcon,
			containerRef,
			viewMoreRef,
			getListItemRef,
			taskListIsExpanded,
		},
		actions: { toggleTaskList },
	};
}
